/**
 * Arrows for the Customer reviews section (sections/shugar-reviews.liquid).
 *
 * The cards sit in a native scroll-snap row, so swiping and scrolling work
 * without this script. It only moves the row one card per arrow click and
 * hides each arrow at its end of the row.
 */
class ShugarReviews extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {number | null} */
  #frame = null;

  connectedCallback() {
    this.#track = this.querySelector('.shugar-reviews__track');
    this.#prev = this.querySelector('.shugar-reviews__arrow--prev');
    this.#next = this.querySelector('.shugar-reviews__arrow--next');
    if (!this.#track) return;

    this.addEventListener('click', this.#onClick);
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll);
    this.#updateArrows();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-reviews-step]');
    if (!(button instanceof HTMLElement) || !this.#track) return;

    const card = this.#track.querySelector('.shugar-reviews__card');
    const gap = parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : this.#track.clientWidth;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#track.scrollBy({
      left: step * Number(button.dataset.reviewsStep),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  #onScroll = () => {
    if (this.#frame === null) {
      this.#frame = requestAnimationFrame(() => {
        this.#frame = null;
        this.#updateArrows();
      });
    }
  };

  #updateArrows() {
    if (!this.#track) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.#track;
    if (this.#prev) this.#prev.disabled = scrollLeft <= 1;
    if (this.#next) this.#next.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
  }
}

if (!customElements.get('shugar-reviews')) {
  customElements.define('shugar-reviews', ShugarReviews);
}
